import { useState, useContext, Component, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Avatar from '@mui/material/Avatar';
import Tooltip from '@mui/material/Tooltip';

import ChatIcon from '@mui/icons-material/ChatBubbleOutlineRounded';
import ArticleIcon from '@mui/icons-material/ArticleOutlined';
import ImageIcon from '@mui/icons-material/ImageOutlined';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonthRounded';
import LogoutIcon from '@mui/icons-material/LogoutRounded';
import MenuIcon from '@mui/icons-material/MenuRounded';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeftRounded';
import ChevronRightIcon from '@mui/icons-material/ChevronRightRounded';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';

import { AuthContext } from "../../contexts/auth";
import api from "../../services/api";
import ModernChat from "../../components/modernChat";
import ModernArticleGenerator from "../../components/modernArticleGenerator";
import ModernImageGenerator from "../../components/modernImageGenerator";
import ModernLinkedInGenerator from "../../components/modernLinkedInGenerator";
import MarketingAgent from "../../components/marketingAgent";
import CalendarPage from "../../components/calendarPage";
import ProfilePage from "../user";
import LegacyMigrationModal from "./LegacyMigrationModal";
import PricingModal from "../../components/pricingModal";
import PayPerUseModal from "../../components/payPerUseModal";
import LanguageSwitcher from "../../components/LanguageSwitcher";
import SocialContactModal from "../../components/socialContactModal";

const ADMIND_LOGO = "https://admindaiacademy.s3.eu-central-1.amazonaws.com/Random+Sheet/Loghi+AdMind/ADMINDLogoNegative.png";

const NAV_ITEMS_BASE = [
    { key: 'agent',        icon: <AutoAwesomeIcon />,       path: "/agent",    highlight: true },
    { key: 'calendar',     icon: <CalendarMonthIcon />,     path: "/calendar" },
    { key: 'chat',         icon: <ChatIcon />,              path: "/chat" },
    { key: 'articles',     icon: <ArticleIcon />,           path: "/articles" },
    { key: 'images',       icon: <ImageIcon />,             path: "/image" },
    { key: 'linkedin',     icon: <LinkedInIcon />,          path: "/linkedin" },
    { key: 'other_social', icon: <AddCircleOutlineIcon />,  modal: 'social_contact' },
];

/* ── Dark sidebar palette ── */
const SB_BG = '#0D0D14';
const SB_BORDER = 'rgba(255,255,255,0.07)';
const SB_TEXT = 'rgba(255,255,255,0.52)';
const SB_TEXT_ACTIVE = '#fff';
const SB_ACTIVE_BG = 'rgba(112,18,128,0.28)';
const SB_HOVER_BG = 'rgba(255,255,255,0.06)';

const SIDEBAR_W = 220;
const SIDEBAR_W_COLLAPSED = 58;

/* ── Error boundary error display (functional = can use hooks) ── */
function ErrorDisplay() {
    const { t } = useTranslation();
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', flexDirection: 'column', gap: 2 }}>
            <Typography sx={{ color: '#FF6B35', fontSize: '1rem' }}>{t('app.error.title')}</Typography>
            <Typography sx={{ color: '#6B6B7B', fontSize: '0.85rem' }}>{t('app.error.subtitle')}</Typography>
        </Box>
    );
}

/* ── These are defined OUTSIDE Application so React sees stable component types ── */

function NavItemDesktop({ item, sidebarOpen, active, onClick }) {
    return (
        <Tooltip title={!sidebarOpen ? item.label : ''} placement="right">
            <Box
                onClick={onClick}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: sidebarOpen ? 1.5 : 0,
                    px: sidebarOpen ? 1.5 : 0,
                    py: 1.1,
                    borderRadius: '10px',
                    cursor: 'pointer',
                    justifyContent: sidebarOpen ? 'flex-start' : 'center',
                    background: active ? SB_ACTIVE_BG : 'transparent',
                    color: active ? SB_TEXT_ACTIVE : SB_TEXT,
                    borderLeft: active ? '2px solid #701280' : '2px solid transparent',
                    transition: 'all 0.15s',
                    '&:hover': {
                        background: active ? SB_ACTIVE_BG : SB_HOVER_BG,
                        color: SB_TEXT_ACTIVE,
                    },
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0, '& svg': { fontSize: '1.15rem' } }}>
                    {item.icon}
                </Box>
                {sidebarOpen && (
                    <>
                        <Typography sx={{ fontSize: '0.875rem', fontWeight: active ? 600 : 400, flex: 1, whiteSpace: 'nowrap', color: 'inherit' }}>
                            {item.label}
                        </Typography>
                        {item.highlight && (
                            <Box sx={{ background: 'linear-gradient(135deg, #701280, #C850C0)', borderRadius: '4px', px: 0.7, py: 0.2, flexShrink: 0 }}>
                                <Typography sx={{ fontSize: '0.6rem', color: '#fff', fontWeight: 700, lineHeight: 1.4 }}>AI</Typography>
                            </Box>
                        )}
                    </>
                )}
            </Box>
        </Tooltip>
    );
}

function NavItemMobile({ item, active, onClick }) {
    return (
        <Box
            onClick={onClick}
            sx={{
                display: 'flex', alignItems: 'center', gap: 1.5,
                px: 1.5, py: 1.1, borderRadius: '10px', cursor: 'pointer',
                transition: 'all 0.15s',
                borderLeft: active ? '2px solid #701280' : '2px solid transparent',
                background: active ? SB_ACTIVE_BG : 'transparent',
                color: active ? SB_TEXT_ACTIVE : SB_TEXT,
                '&:hover': { background: SB_HOVER_BG, color: SB_TEXT_ACTIVE },
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', '& svg': { fontSize: '1.15rem' } }}>{item.icon}</Box>
            <Typography sx={{ fontSize: '0.875rem', fontWeight: active ? 600 : 400, flex: 1, color: 'inherit' }}>{item.label}</Typography>
            {item.highlight && (
                <Box sx={{ background: 'linear-gradient(135deg, #701280, #C850C0)', borderRadius: '4px', px: 0.7, py: 0.2 }}>
                    <Typography sx={{ fontSize: '0.6rem', color: '#fff', fontWeight: 700, lineHeight: 1.4 }}>AI</Typography>
                </Box>
            )}
        </Box>
    );
}

class ContentErrorBoundary extends Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false };
    }
    static getDerivedStateFromError() { return { hasError: true }; }
    render() {
        if (this.state.hasError) return <ErrorDisplay />;
        return this.props.children;
    }
}

const getComponent = (path) => {
    if (path.includes('/chat')) return <ModernChat />;
    if (path.includes('/articles')) return <ModernArticleGenerator />;
    if (path.includes('/image')) return <ModernImageGenerator />;
    if (path.includes('/linkedin')) return <ModernLinkedInGenerator />;
    if (path.includes('/calendar')) return <CalendarPage />;
    if (path.includes('/user')) return <ProfilePage />;
    return <MarketingAgent />;
};

function Application({ userState }) {
    const navigate = useNavigate();
    const location = useLocation();
    const { user, signOut } = useContext(AuthContext);
    const { t } = useTranslation();

    const [mobileOpen, setMobileOpen] = useState(false);
    const [legacyForceOpen, setLegacyForceOpen] = useState(false);
    const [isLegacyUser, setIsLegacyUser] = useState(null);
    const [sidebarOpen, setSidebarOpen] = useState(() => {
        try { return localStorage.getItem('admind_sidebar') !== 'closed'; } catch { return true; }
    });
    const [pricingModalOpen, setPricingModalOpen] = useState(false);
    const [pricingModalCurrentPlan, setPricingModalCurrentPlan] = useState(null);
    const [payPerUseModalOpen, setPayPerUseModalOpen] = useState(false);
    const [proUserHasStripeCustomer, setProUserHasStripeCustomer] = useState(true);
    const [socialContactOpen, setSocialContactOpen] = useState(false);

    const NAV_ITEMS = NAV_ITEMS_BASE.map(item => ({
        ...item,
        label: t(`app.nav.${item.key}`),
    }));

    useEffect(() => {
        const handleLimitExceeded = (e) => {
            const { code, has_stripe_customer, current_plan } = e.detail || {};
            if (code === 'pro_limit_exceeded') {
                if (current_plan === 'pro_max') {
                    setProUserHasStripeCustomer(has_stripe_customer !== false);
                    setPayPerUseModalOpen(true);
                } else {
                    setPricingModalCurrentPlan(current_plan || 'pro_light');
                    setPricingModalOpen(true);
                }
            } else {
                setPricingModalCurrentPlan(null);
                setPricingModalOpen(true);
            }
        };
        window.addEventListener('admind_limit_exceeded', handleLimitExceeded);
        return () => window.removeEventListener('admind_limit_exceeded', handleLimitExceeded);
    }, []);

    // Sync subscription after Stripe checkout redirect (fallback for webhook delays)
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const stripeStatus = params.get('stripe');
        const sessionId = params.get('session_id');
        if (stripeStatus === 'success' && sessionId && sessionId.startsWith('cs_')) {
            api.post('/billing/stripe/checkout-sync/', { session_id: sessionId })
                .catch(() => {});
        }
    }, [location.search]);

    const toggleSidebar = () => {
        setSidebarOpen(prev => {
            const next = !prev;
            try { localStorage.setItem('admind_sidebar', next ? 'open' : 'closed'); } catch { }
            return next;
        });
    };

    const handleSignOut = () => { signOut(); navigate('/login'); };

    const firstName = user?.first_name || user?.email?.split('@')[0] || 'Account';
    const initials = firstName.slice(0, 1).toUpperCase();

    const isActive = (path) => {
        if (path === '/agent') return location.pathname === '/agent' || location.pathname === '/dashboard' || location.pathname === '/autopilot';
        return location.pathname === path;
    };

    return (
        <Box sx={{ display: 'flex', height: '100vh', backgroundColor: '#FAFAF8' }}>
            <LegacyMigrationModal
                forceOpen={legacyForceOpen}
                onForceClose={() => setLegacyForceOpen(false)}
                onLegacyStatus={(v) => setIsLegacyUser(v)}
            />

            <PricingModal
                open={pricingModalOpen}
                onClose={() => setPricingModalOpen(false)}
                currentPlan={pricingModalCurrentPlan}
                onSuccess={() => setPricingModalOpen(false)}
            />

            <PayPerUseModal
                open={payPerUseModalOpen}
                onClose={() => setPayPerUseModalOpen(false)}
                isPro={true}
                hasStripeCustomer={proUserHasStripeCustomer}
                onActivated={() => setPayPerUseModalOpen(false)}
            />

            <SocialContactModal
                open={socialContactOpen}
                onClose={() => setSocialContactOpen(false)}
                user={user}
            />

            {/* ── Desktop Sidebar ── */}
            <Box
                component="nav"
                sx={{
                    width: sidebarOpen ? SIDEBAR_W : SIDEBAR_W_COLLAPSED,
                    flexShrink: 0,
                    display: { xs: 'none', md: 'flex' },
                    flexDirection: 'column',
                    background: SB_BG,
                    borderRight: `1px solid ${SB_BORDER}`,
                    height: '100vh',
                    position: 'sticky',
                    top: 0,
                    overflow: 'hidden',
                    transition: 'width 0.22s cubic-bezier(0.4,0,0.2,1)',
                    boxShadow: '4px 0 40px rgba(112,18,128,0.10)',
                }}
            >
                {/* Header */}
                <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: sidebarOpen ? 'space-between' : 'center',
                    px: sidebarOpen ? 2 : 0,
                    py: 2,
                    flexShrink: 0,
                    borderBottom: `1px solid ${SB_BORDER}`,
                    minHeight: 58,
                }}>
                    {sidebarOpen ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', overflow: 'hidden', pl: 0.5 }}>
                            <img src={ADMIND_LOGO} alt="Admind" style={{ height: 30, display: 'block' }} />
                        </Box>
                    ) : (
                        <Box sx={{
                            width: 32, height: 32, borderRadius: '10px',
                            background: 'linear-gradient(135deg, #701280, #C850C0)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                            <AutoAwesomeIcon sx={{ fontSize: '1rem', color: '#fff' }} />
                        </Box>
                    )}
                    <Tooltip title={sidebarOpen ? t('app.sidebar.collapse') : t('app.sidebar.expand')} placement="right">
                        <IconButton
                            size="small"
                            onClick={toggleSidebar}
                            sx={{
                                color: SB_TEXT, borderRadius: '8px',
                                border: `1px solid ${SB_BORDER}`, width: 28, height: 28,
                                background: 'rgba(255,255,255,0.05)',
                                '&:hover': { color: '#fff', borderColor: 'rgba(112,18,128,0.5)', background: 'rgba(112,18,128,0.2)' },
                                transition: 'all 0.15s',
                            }}
                        >
                            {sidebarOpen ? <ChevronLeftIcon sx={{ fontSize: '1rem' }} /> : <ChevronRightIcon sx={{ fontSize: '1rem' }} />}
                        </IconButton>
                    </Tooltip>
                </Box>

                {/* Nav Items */}
                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 0.5, py: 2, px: sidebarOpen ? 1.5 : 0.75, overflowY: 'auto', overflowX: 'hidden' }}>
                    {NAV_ITEMS.map((item) => (
                        <NavItemDesktop
                            key={item.path || item.modal}
                            item={item}
                            sidebarOpen={sidebarOpen}
                            active={item.path ? isActive(item.path) : false}
                            onClick={() => {
                                if (item.modal === 'social_contact') setSocialContactOpen(true);
                                else navigate(item.path);
                            }}
                        />
                    ))}
                </Box>

                {/* Footer */}
                <Box sx={{ borderTop: `1px solid ${SB_BORDER}`, pt: 1.5, pb: 2, display: 'flex', flexDirection: 'column', gap: 0.5, px: sidebarOpen ? 1.5 : 0.75 }}>
                    {/* Language switcher */}
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: sidebarOpen ? 'flex-start' : 'center',
                        mb: 0.5,
                    }}>
                        {sidebarOpen ? (
                            <LanguageSwitcher dark={true} openUp={true} />
                        ) : (
                            <Tooltip title={t('app.sidebar.language')} placement="right">
                                <Box sx={{ cursor: 'pointer', fontSize: '1.1rem', py: 0.5 }}>🌐</Box>
                            </Tooltip>
                        )}
                    </Box>

                    {/* User chip */}
                    <Box
                        onClick={() => navigate('/user')}
                        sx={{
                            display: 'flex', alignItems: 'center',
                            gap: sidebarOpen ? 1.5 : 0,
                            px: sidebarOpen ? 1 : 0, py: 1,
                            justifyContent: sidebarOpen ? 'flex-start' : 'center',
                            borderRadius: '12px',
                            background: sidebarOpen ? 'rgba(255,255,255,0.04)' : 'transparent',
                            border: sidebarOpen ? `1px solid ${SB_BORDER}` : 'none',
                            cursor: 'pointer',
                            transition: 'background 0.15s',
                            '&:hover': {
                                background: sidebarOpen ? 'rgba(255,255,255,0.08)' : 'rgba(255,255,255,0.06)',
                                border: sidebarOpen ? `1px solid rgba(200,80,192,0.3)` : 'none',
                            },
                        }}
                    >
                        <Tooltip title={t('app.sidebar.profile')} placement="right">
                            <Avatar sx={{ width: 30, height: 30, fontSize: '0.75rem', background: 'linear-gradient(135deg, #701280, #C850C0)', fontWeight: 700, flexShrink: 0 }}>
                                {initials}
                            </Avatar>
                        </Tooltip>
                        {sidebarOpen && (
                            <>
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{firstName}</Typography>
                                    <Typography sx={{ fontSize: '0.68rem', color: '#C850C0', fontWeight: 600 }}>{user?.subscription || 'Free'}</Typography>
                                </Box>
                                <Tooltip title={t('app.sidebar.logout')}>
                                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleSignOut(); }} sx={{ color: SB_TEXT, '&:hover': { color: '#FF6B35' }, p: 0.5 }}>
                                        <LogoutIcon sx={{ fontSize: '0.95rem' }} />
                                    </IconButton>
                                </Tooltip>
                            </>
                        )}
                    </Box>
                </Box>
            </Box>

            {/* ── Mobile Drawer Overlay ── */}
            {mobileOpen && (
                <Box sx={{ position: 'fixed', inset: 0, zIndex: 1200, display: { xs: 'flex', md: 'none' } }}>
                    <Box onClick={() => setMobileOpen(false)} sx={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)' }} />
                    <Box sx={{ position: 'relative', width: SIDEBAR_W, background: SB_BG, height: '100%', borderRight: `1px solid ${SB_BORDER}`, zIndex: 1, display: 'flex', flexDirection: 'column', py: 2.5, px: 1.5 }}>
                        <Box sx={{ px: 1, mb: 3 }}>
                            <img src={ADMIND_LOGO} alt="Admind" style={{ height: 30, display: 'block' }} />
                        </Box>
                        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                            {NAV_ITEMS.map((item) => (
                                <NavItemMobile
                                    key={item.path || item.modal}
                                    item={item}
                                    active={item.path ? isActive(item.path) : false}
                                    onClick={() => {
                                        if (item.modal === 'social_contact') {
                                            setSocialContactOpen(true);
                                            setMobileOpen(false);
                                        } else {
                                            navigate(item.path);
                                            setMobileOpen(false);
                                        }
                                    }}
                                />
                            ))}
                        </Box>
                        <Box sx={{ borderTop: `1px solid ${SB_BORDER}`, pt: 2, mt: 1 }}>
                            {/* Language switcher mobile */}
                            <Box sx={{ mb: 1 }}>
                                <LanguageSwitcher dark={true} openUp={true} />
                            </Box>

                            <Box
                                onClick={() => { navigate('/user'); setMobileOpen(false); }}
                                sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 1.5, py: 1, borderRadius: '12px', cursor: 'pointer', '&:hover': { background: 'rgba(255,255,255,0.06)' } }}
                            >
                                <Avatar sx={{ width: 30, height: 30, fontSize: '0.75rem', background: 'linear-gradient(135deg, #701280, #C850C0)', fontWeight: 700, flexShrink: 0 }}>{initials}</Avatar>
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{firstName}</Typography>
                                    <Typography sx={{ fontSize: '0.7rem', color: '#C850C0', fontWeight: 600 }}>{user?.subscription || 'Free'}</Typography>
                                </Box>
                                <Tooltip title={t('app.sidebar.logout')}>
                                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleSignOut(); }} sx={{ color: SB_TEXT, '&:hover': { color: '#FF6B35' } }}>
                                        <LogoutIcon sx={{ fontSize: '1rem' }} />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        </Box>
                    </Box>
                </Box>
            )}

            {/* ── Main Content ── */}
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, overflow: 'hidden' }}>
                {/* Mobile top bar */}
                <Box sx={{
                    display: { xs: 'flex', md: 'none' },
                    alignItems: 'center', gap: 1.5,
                    px: 2, py: 1.5,
                    borderBottom: '1px solid rgba(0,0,0,0.07)',
                    backgroundColor: SB_BG,
                }}>
                    <IconButton onClick={() => setMobileOpen(true)} sx={{ color: 'rgba(255,255,255,0.7)' }}>
                        <MenuIcon />
                    </IconButton>
                    <img src={ADMIND_LOGO} alt="Admind" style={{ height: 24 }} />
                </Box>

                <Box sx={{ flex: 1, overflow: 'auto', display: 'flex', flexDirection: 'column', backgroundColor: '#FAFAF8' }}>
                    <ContentErrorBoundary>
                        {getComponent(location.pathname)}
                    </ContentErrorBoundary>
                </Box>
            </Box>
        </Box>
    );
}

export default Application;
